import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import serviceApi from '../../services/serviceApi';
import { formatSAR } from '../../utils/currencyFormatter';
import { CUSTOM_PRICING_UI_ENABLED } from './cleaningPricingConfig';

/**
 * Subscription package selector — fetches catalog once, filters by the
 * location's pricingTier. Calls onChange with the selected packageCode
 * (or '' when "custom pricing" is chosen via the empty option).
 *
 * Per architecture §5.1: dropdown filtered by location's pricingTier.
 */
const PackageSelector = ({ locationTier, value, onChange, disabled = false, label = 'الباقة *' }) => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await serviceApi.getCatalog();
        if (!cancelled) setCatalog(res.data.catalog);
      } catch (err) {
        if (!cancelled) setError('تعذر تحميل قائمة الباقات');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadCatalog();
    return () => { cancelled = true; };
  }, []);

  const tierPackages = useMemo(() => {
    if (!catalog || !locationTier) return [];
    return Object.values(catalog.packages || {}).filter((p) => p.pricingTier === locationTier);
  }, [catalog, locationTier]);

  return (
    <Form.Group className="mb-3">
      <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>{label}</Form.Label>
      <Form.Select
        className="touch-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading || !locationTier}
      >
        <option value="">
          {locationTier
            ? (loading
                ? 'جاري التحميل...'
                : (CUSTOM_PRICING_UI_ENABLED ? 'سعر مخصص (بدون باقة)' : 'اختر الباقة...'))
            : 'اختر الموقع أولاً...'}
        </option>
        {tierPackages.map((p) => (
          <option key={p.packageCode} value={p.packageCode}>
            {p.packageName} — {p.visitsIncluded} زيارة، {p.validityDays} يوم — {formatSAR(p.listPrice)}
          </option>
        ))}
      </Form.Select>
      {error && (
        <div style={{ fontSize: '0.78rem', color: '#dc3545', marginTop: 4 }}>{error}</div>
      )}
      {!loading && locationTier && tierPackages.length === 0 && !error && (
        <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: 4 }}>
          لا توجد باقات لفئة هذا الموقع — يمكنك استخدام السعر المخصص
        </div>
      )}
    </Form.Group>
  );
};

export default PackageSelector;
